import type { Ref } from 'react'
import type ReCAPTCHA from 'react-google-recaptcha'
import type { Logger } from '@/scopes/common/domain/types'
import { useImperativeHandle, useRef, useState } from 'react'
import GoogleReCAPTCHA from 'react-google-recaptcha'
import { useCommonDependency } from '@/scopes/common/infrastructure/dependencies/common'
import { useEnv } from '@/scopes/common/infrastructure/env'

export interface InvisibleRecaptchaHandle {
  execute: () => Promise<string | undefined>
}

interface ScriptLoadState {
  loaded?: boolean
}

const SCRIPT_LOAD_TIMEOUT_MS = 5000

type ScriptStatus = 'loaded' | 'blocked' | 'timeout'

const NOT_READY_ERROR_MESSAGE: Record<Exclude<ScriptStatus, 'loaded'>, string> = {
  blocked: 'reCAPTCHA not ready, submitting without token (script failed to load)',
  timeout: 'reCAPTCHA not ready, submitting without token (script load timeout)',
}

async function requestToken(widget: ReCAPTCHA, logger: Logger) {
  try {
    const token = await widget.executeAsync()

    return token || undefined
  }
  catch {
    logger.logError(new Error('reCAPTCHA token request failed'))
    return undefined
  }
  finally {
    widget.reset()
  }
}

export function InvisibleRecaptcha({ ref }: { ref?: Ref<InvisibleRecaptchaHandle> }) {
  const widgetRef = useRef<ReCAPTCHA>(null)
  const resolveScriptLoadRef = useRef<(status: ScriptStatus) => void>(() => {})
  const [scriptLoaded] = useState(() => new Promise<ScriptStatus>((resolve) => {
    resolveScriptLoadRef.current = resolve
  }))
  const siteKey = useEnv('RECAPTCHA_SITE_KEY')
  const logger = useCommonDependency('logger')

  useImperativeHandle(ref, () => ({
    execute: async () => {
      const widget = widgetRef.current

      if (!widget)
        return undefined

      let timeoutId: ReturnType<typeof setTimeout> | undefined
        const scriptStatus = await Promise.race([
        scriptLoaded,
        new Promise<ScriptStatus>((resolve) => {
            timeoutId = setTimeout(resolve, SCRIPT_LOAD_TIMEOUT_MS, 'timeout')
      }),
        ]).finally(() => clearTimeout(timeoutId))

      if (scriptStatus !== 'loaded') {
        logger.logError(new Error(NOT_READY_ERROR_MESSAGE[scriptStatus]))
        return undefined
      }

      return requestToken(widget, logger)
    },
  }), [logger, scriptLoaded])

  function handleScriptLoad({ loaded = false }: ScriptLoadState = {}) {
    resolveScriptLoadRef.current(loaded ? 'loaded' : 'blocked')
  }

  if (!siteKey)
    return null

  return (
    <GoogleReCAPTCHA
      ref={widgetRef}
      sitekey={siteKey}
      size="invisible"
      asyncScriptOnLoad={handleScriptLoad}
    />
  )
}
